import { createRouter, createWebHashHistory } from "vue-router";
import routes from "@/router/routes";
import Cookies from "js-cookie";
import { titleize } from "@/helpers/filters";
import {
  disabledPlatformFeatureFallback,
  disabledPlatformFeatureMessage,
  matchedPlatformFeatureKey,
} from "@/helpers/platformFeatures";
import { useAppStateStore } from "@/stores/AppStateStore";
import { useErrorStore } from "@/stores/ErrorStore";
import { useUserStore } from "@/stores/UserStore";
import { usePlatformFeatureStore } from "@/stores/PlatformFeatureStore";
import { logPageVisit } from "@/helpers/userEvents";
import { useResearchStudioStore } from "@/stores/ResearchStudioStore";
import { useResearchStore } from "@/stores/ResearchStore";
import { useThemeStore } from "@/stores/ThemeStore";

const router = createRouter({
  history: createWebHashHistory(),
  routes,
  scrollBehavior() {
    // always scroll to top
    return { left: 0, top: 0 };
  },
});

let navInfo = undefined;
router.options.history.listen((_to, _from, info) => {
  navInfo = info;
});

const authenticated = () => {
  const userStore = useUserStore();
  return !!Cookies.get("access_token") && !!userStore.currentUser;
};

const authorized = (to) => {
  const userStore = useUserStore();
  if (to.meta.permissions && to.meta.permissions.length > 0) {
    let permissions = userStore.currentUser?.permissions.map((u) => u.name);
    if (userStore.currentAppClient) {
      permissions = userStore.currentUser?.permissions
        .filter((p) => !p.paid_product)
        .map((u) => u.name);
      permissions = [
        ...permissions,
        ...(userStore.currentUser?.products?.map((p) => p.name) ?? []),
      ];
    }
    if (to.meta.requireAllPermissions) {
      return (
        permissions && to.meta.permissions.every((p) => permissions.includes(p))
      );
    } else {
      return (
        permissions && to.meta.permissions.some((p) => permissions.includes(p))
      );
    }
  }
  return true;
};

const navigate = async (to, from) => {
  const userStore = useUserStore();
  const platformFeatureStore = usePlatformFeatureStore();

  if (userStore?.currentUser?.roles?.every((r) => r.maintenance_mode)) {
    return "/maintenance";
  }

  if (!platformFeatureStore.loaded) {
    try {
      await platformFeatureStore.loadStatus();
    } catch {
      // Fail open: missing status leaves all features enabled.
    }
  }

  const featureKey = matchedPlatformFeatureKey(to);
  if (featureKey && platformFeatureStore.isDisabled(featureKey)) {
    const errorStore = useErrorStore();
    errorStore.showError(
      disabledPlatformFeatureMessage(
        platformFeatureStore.nameFor(featureKey),
        featureKey,
      ),
      false,
    );
    const fromFeatureKey = matchedPlatformFeatureKey(from);
    const fromIsUsable =
      from.matched?.length > 0 &&
      from.name &&
      from.name !== to.name &&
      !platformFeatureStore.isDisabled(fromFeatureKey);
    if (fromIsUsable) return false;
    return disabledPlatformFeatureFallback(to.name);
  }

  if (authorized(to)) {
    if (!to?.meta?.customLogging) {
      logPageVisit(to, from);
    }
    updateBreadcrumbs(to, from);
    return;
  }
  return { name: "home", query: { 403: to.name } };
};

const updateBreadcrumbs = async (to, from) => {
  const appStateStore = useAppStateStore();
  // TODO: need to handle this on page refresh and when the survey is changed
  if (to.meta.breadcrumb === "survey_name") {
    const researchStudioStore = useResearchStudioStore();
    while (researchStudioStore.loading.surveys) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    if (researchStudioStore.surveys.length) {
      const survey = researchStudioStore.surveys.find(
        (s) => s.id === parseInt(to.params.survey_id),
      );
      to.meta.breadcrumb = survey.name;
    }
  }

  // research_results reuses one route across kpi/survey/campaign tabs, so
  // (unlike survey_name above) this must be recomputed on every visit rather
  // than gated on the meta still holding a sentinel value. Its own
  // breadcrumb entry always stays "Research Results" (see routes.js); when
  // viewing a survey, its name is appended as a trailing crumb rather than
  // replacing "Research Results", so the trail reads
  // Research / Research Results / <survey name>.
  let researchResultsSurveyName;
  if (to.name === "research_results") {
    const surveyId =
      to.query.tab === "survey" ? parseInt(to.query.survey_id) : NaN;
    if (!Number.isNaN(surveyId)) {
      const researchStore = useResearchStore();
      while (researchStore.loading.surveys) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      researchResultsSurveyName = researchStore.surveys.find(
        (s) => s.id === surveyId,
      )?.name;
    }
  }

  if (navInfo?.type === "pop") {
    appStateStore.breadcrumbs = [];
    navInfo = undefined;
  }

  if (!appStateStore.breadcrumbs.length) {
    to.matched.forEach((route, idx) => {
      if (idx === 0) return;
      if (idx > 1 && idx < to.matched.length - 1) {
        if (to.matched[idx + 1].path !== route.path) {
          appStateStore.breadcrumbs.push(route.meta.breadcrumb);
        }
      } else if (route.meta.breadcrumb) {
        appStateStore.breadcrumbs.push(route.meta.breadcrumb);
      } else if (route.name) {
        appStateStore.breadcrumbs.push(titleize(route.name.replace(/-/, " ")));
      }
    });
    if (to.name === "survey_details") {
      const numBreadcrumbs = appStateStore.breadcrumbs.length;
      appStateStore.breadcrumbs[numBreadcrumbs - 1] = to.meta.breadcrumb;
    } else if (to.name === "research_results" && researchResultsSurveyName) {
      appStateStore.breadcrumbs.push(researchResultsSurveyName);
    }
  } else {
    if (from.name === to.name) {
      if (to.name === "survey_details") {
        // handle changing survey dropdown on details page
        const numBreadcrumbs = appStateStore.breadcrumbs.length;
        appStateStore.breadcrumbs[numBreadcrumbs - 1] = to.meta.breadcrumb;
        return;
      }
      if (to.name === "research_results") {
        // "Research" / "Research Results" never change for this route; only
        // the trailing survey-name crumb (if any) needs to be refreshed.
        appStateStore.breadcrumbs = appStateStore.breadcrumbs.slice(0, 2);
        if (researchResultsSurveyName) {
          appStateStore.breadcrumbs.push(researchResultsSurveyName);
        }
        return;
      }
      return;
    }
    appStateStore.breadcrumbs = [...appStateStore.breadcrumbs];
    appStateStore.breadcrumbs.pop();
    if (from.meta?.breadcrumb?.to) {
      appStateStore.breadcrumbs.push(from.meta.breadcrumb);
    } else {
      appStateStore.breadcrumbs.push({
        title: from.meta.breadcrumb || titleize(from.name.replace(/-/, " ")),
        to: { name: from.name },
      });
    }
    if (to.meta.breadcrumb) {
      appStateStore.breadcrumbs.push(to.meta.breadcrumb);
    } else if (to.name) {
      appStateStore.breadcrumbs.push(titleize(to.name.replace(/-/, " ")));
    }
    if (to.name === "research_results" && researchResultsSurveyName) {
      appStateStore.breadcrumbs.push(researchResultsSurveyName);
    }
  }
};

const getBrandForTitle = () => {
  const themeStore = useThemeStore();
  if (themeStore.activeTheme) return themeStore.brand;
  return /\.deeprootanalytics\.com$/.test(window.location.hostname)
    ? "Deep Root"
    : "Tunnl";
};

router.beforeEach(async (to, from) => {
  const userStore = useUserStore();
  document.title = `${getBrandForTitle()} ${to.meta.title || "Platform"}`;
  if (to.meta.auth && !authenticated()) {
    const result = await userStore.getCurrentUser();
    if (result === false) {
      return false;
    }
    return navigate(to, from);
  }
  if (to.meta.auth) {
    return navigate(to, from);
  }
});

export default router;
